<script setup lang="ts">
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { ExportDocumentFormat } from "~/types/enums/ExportDocumentFormat.enum";

const props = defineProps<{ documentId: string }>();
const isDownloading = ref(false);
const includeChecklist = ref(true);

const download = async (format: ExportDocumentFormat.pdf | ExportDocumentFormat.docx) => {
  if (isDownloading.value) return;
  isDownloading.value = true;
  try {
    await useDownloadStudentDraft(props.documentId, format, includeChecklist.value);
    useBaseToast("Download started.", "success");
  } catch {
    useBaseToast("We couldn’t download your document. Please try again.", "error");
  } finally {
    isDownloading.value = false;
  }
};
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        v-tooltip="'Download document'"
        type="button"
        aria-label="Download document"
        :aria-busy="isDownloading"
        :disabled="isDownloading"
        class="mr-2 flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded text-gray-600 transition-colors hover:bg-medium-default/10 focus-visible:ring-2 focus-visible:ring-primary-default disabled:opacity-50"
      >
        <icon v-if="isDownloading" name="material-symbols:sync" class="h-3.5 w-3.5 shrink-0 animate-spin" />
        <icon v-else name="lucide:download" class="h-3.5 w-3.5 shrink-0" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" class="w-52">
      <DropdownMenuItem
        role="menuitemcheckbox"
        :aria-checked="includeChecklist"
        @select.prevent="includeChecklist = !includeChecklist"
      >
        <BaseCheckbox
          :model-value="includeChecklist"
          read-only
          size="small"
          label="Include checklist"
          class="pointer-events-none"
        />
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem @select="download(ExportDocumentFormat.pdf)">
        <icon name="grommet-icons:document-pdf" class="text-[#FF0000]" />
        Download PDF
      </DropdownMenuItem>
      <DropdownMenuItem @select="download(ExportDocumentFormat.docx)">
        <icon name="file-icons:microsoft-word" class="text-[#2B579A]" />
        Download Word
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
